import { EventEmitter } from '../../core/Event';
import type { Random } from '../../core/Random';
import type { ScratchEvent } from './ScratchEvents';
import type {
  ScratchConfig,
  ScratchOutcome,
  ScratchReward,
  ScratchSymbol,
} from './ScratchTypes';

/** Outcome-band probabilities — must sum to 1. */
const OUTCOME_BANDS: readonly { outcome: ScratchOutcome; weight: number }[] = [
  { outcome: 'lose', weight: 0.6 },
  { outcome: 'small', weight: 0.3 },
  { outcome: 'big', weight: 0.1 },
];

/** Coin payout ranges per outcome band. Inclusive endpoints. */
const COIN_RANGES: Record<ScratchOutcome, readonly [number, number]> = {
  lose: [0, 0],
  small: [25, 100],
  big: [200, 250],
};

/** Gem payout ranges per outcome band. Inclusive endpoints. */
const GEM_RANGES: Record<ScratchOutcome, readonly [number, number]> = {
  lose: [0, 0],
  small: [0, 0],
  big: [1, 2],
};

/**
 * Authoritative state for a scratch-card session: the rolled outcome, the
 * generated board, and which cells have been revealed. The reward is
 * pre-computed at construction time so the {@code scratch-complete}
 * payout matches whatever board the player ends up looking at.
 *
 * <p>Win condition is "3-of-any-symbol on the board" — pre-rolling the
 * outcome and constructing the board to match keeps this provably
 * consistent (a lose board has every symbol capped at 2; a small / big
 * win board has exactly one symbol with ≥3 occurrences).
 */
export class ScratchModel {
  public readonly events = new EventEmitter<ScratchEvent>();
  public readonly cellCount: number;
  public readonly columnCount: number;
  private readonly board: ScratchSymbol[];
  private readonly revealed = new Set<number>();
  private readonly reward: ScratchReward;
  private readonly matchedSymbol: ScratchSymbol | null;
  private completed = false;

  constructor(config: ScratchConfig, random: Random) {
    this.cellCount = config.cellCount ?? 9;
    this.columnCount = config.columnCount ?? 3;
    if (config.symbols.length < 5) {
      throw new Error(
        `ScratchModel needs ≥5 symbols to support a "lose" board over ` +
          `${this.cellCount} cells (got ${config.symbols.length}).`,
      );
    }

    const outcome = pickOutcome(random);
    this.reward = rollReward(outcome, random);
    const built = buildBoard(outcome, config.symbols, this.cellCount, random);
    this.board = built.board;
    this.matchedSymbol = built.matchedSymbol;
  }

  /** Symbol at {@code index} after a reveal. Null while still hidden — for
   * replay / state restore. */
  symbolAt(index: number): ScratchSymbol | null {
    if (!this.revealed.has(index)) return null;
    return this.board[index] ?? null;
  }

  isRevealed(index: number): boolean {
    return this.revealed.has(index);
  }

  cellsRemaining(): number {
    return this.cellCount - this.revealed.size;
  }

  isComplete(): boolean {
    return this.completed;
  }

  /** Reveal cell {@code index}. No-op if already revealed or the card is
   * already complete. Returns the symbol on success, null otherwise.
   * Emits {@code cell-revealed}, then {@code scratch-complete} when the
   * final cell is revealed. */
  reveal(index: number): ScratchSymbol | null {
    if (this.completed) return null;
    if (index < 0 || index >= this.cellCount) return null;
    if (this.revealed.has(index)) return null;

    const symbol = this.board[index]!;
    this.revealed.add(index);
    const cellsRemaining = this.cellCount - this.revealed.size;
    this.events.emit({ kind: 'cell-revealed', index, symbol, cellsRemaining });

    if (cellsRemaining === 0) {
      this.completed = true;
      this.events.emit({
        kind: 'scratch-complete',
        coins: this.reward.coins,
        gems: this.reward.gems,
        outcome: this.reward.outcome,
        matchedSymbol: this.matchedSymbol,
      });
    }
    return symbol;
  }

  dispose(): void {
    this.events.clear();
  }
}

function pickOutcome(random: Random): ScratchOutcome {
  const r = random.nextFloat();
  let acc = 0;
  for (const { outcome, weight } of OUTCOME_BANDS) {
    acc += weight;
    if (r < acc) return outcome;
  }
  return OUTCOME_BANDS[OUTCOME_BANDS.length - 1]!.outcome;
}

function rollReward(outcome: ScratchOutcome, random: Random): ScratchReward {
  const [coinMin, coinMax] = COIN_RANGES[outcome];
  const [gemMin, gemMax] = GEM_RANGES[outcome];
  return {
    outcome,
    coins: coinMin === coinMax ? coinMin : random.nextIntBetween(coinMin, coinMax + 1),
    gems: gemMin === gemMax ? gemMin : random.nextIntBetween(gemMin, gemMax + 1),
  };
}

interface BuiltBoard {
  board: ScratchSymbol[];
  /** The symbol with ≥3 occurrences (winner). Null on a lose board. */
  matchedSymbol: ScratchSymbol | null;
}

/**
 * Construct a {@code cellCount}-length board that guarantees the rolled
 * outcome's win condition (no triple for lose; exactly one triple for
 * small/big). The board is then shuffled so the matching cells land in
 * arbitrary positions — the player can't predict the win cells from
 * symbol order.
 */
function buildBoard(
  outcome: ScratchOutcome,
  symbols: readonly ScratchSymbol[],
  cellCount: number,
  random: Random,
): BuiltBoard {
  if (outcome === 'lose') {
    // Spread symbols so no symbol appears 3+ times. With ≥5 symbols and
    // 9 cells that's 2+2+2+2+1 (or similar). Cap each at 2 and fill.
    const counts = capAtTwo(symbols.length, cellCount, random);
    const board = expandCounts(symbols, counts);
    return { board: shuffleArray(board, random), matchedSymbol: null };
  }
  // Small / big: pick one winning symbol with exactly 3 occurrences.
  // Distribute the remaining cellCount-3 cells across the other symbols
  // with each capped at 2 (decoy doubles add tension during reveal).
  const winnerIndex = random.nextIntBetween(0, symbols.length);
  const winner = symbols[winnerIndex]!;
  const others = symbols.filter((_, i) => i !== winnerIndex);
  const remaining = cellCount - 3;
  const otherCounts = capAtTwo(others.length, remaining, random);
  const board: ScratchSymbol[] = [winner, winner, winner];
  board.push(...expandCounts(others, otherCounts));
  return { board: shuffleArray(board, random), matchedSymbol: winner };
}

/** Distribute {@code total} into {@code slotCount} buckets where each bucket
 * is capped at 2. Throws if it can't fit. */
function capAtTwo(slotCount: number, total: number, random: Random): number[] {
  if (total > slotCount * 2) {
    throw new Error(
      `capAtTwo: cannot distribute ${total} across ${slotCount} buckets at ≤2 each`,
    );
  }
  const counts = new Array<number>(slotCount).fill(0);
  // Random fill — keep adding to a random non-full bucket until total runs out.
  let placed = 0;
  while (placed < total) {
    const i = random.nextIntBetween(0, slotCount);
    if (counts[i]! < 2) {
      counts[i]! += 1;
      placed += 1;
    }
  }
  return counts;
}

function expandCounts<T>(items: readonly T[], counts: readonly number[]): T[] {
  const out: T[] = [];
  for (let i = 0; i < items.length; i++) {
    const n = counts[i] ?? 0;
    for (let j = 0; j < n; j++) out.push(items[i]!);
  }
  return out;
}

function shuffleArray<T>(arr: T[], random: Random): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = random.nextIntBetween(0, i + 1);
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}
